/**
 * sharingLedgerModel — pure derivation helpers over the Sharing registry's
 * ledger view-model (design §9's `full|basic|off` vocabulary, `state == null`
 * = Review, over an independent per-depth `readiness` axis).
 *
 * No DOM, no host access, no mutation — every function reads its `unit`/
 * `group`/`units` argument and returns a plain value. `SharingRegistry.js`
 * and `SharingDetailPanel.js` both derive their rendering off these, so the
 * vocabulary can only drift in one place.
 *
 * @module sections/partnerSharing/sharingLedgerModel
 */

/** Pill order the toggle/segment renders in — mirrors the legacy
 * `_renderShareStateButtons` off/basics/full order (`PartnerSection.js`). */
const DEPTH_ORDER = ['basic', 'full'];

/**
 * The state pill a ledger row/panel header shows for a unit.
 *
 * `state == null` (never reviewed, or reverted by a register revision) is
 * the fourth pill, `'review'` — it is not itself a consent state.
 *
 * @param {{state: ('full'|'basic'|'off'|null)}} unit
 * @returns {'full'|'basic'|'off'|'review'}
 */
export function pillFor(unit) {
  return unit.state ?? 'review';
}

/**
 * The depth the detail panel's toggle opens pre-selected to for a
 * never-reviewed unit — a *readiness* read, never a consent read.
 *
 * Only a `'verified'` depth may be pre-selected (R12(ii); addendum §5): a
 * `'pending'`/`'failed'`/`'unavailable'` depth is never offered as the
 * starting selection, even if it would otherwise be preferable. Full is
 * preferred to Basic when both are verified. A unit with no verified depth
 * at all returns `null` — that's a readiness state, not a consent state:
 * the panel offers Off only and renders no Approve affordance.
 *
 * @param {{readiness: {full?: string, basic?: string}}} unit
 * @returns {'full'|'basic'|null}
 */
export function preselectDepth(unit) {
  if (unit.readiness?.full === 'verified') return 'full';
  if (unit.readiness?.basic === 'verified') return 'basic';
  return null;
}

/**
 * The depth values the toggle/segment offers for a unit: `'off'` always,
 * plus each of `'basic'`/`'full'` only when that depth's readiness is
 * `'verified'`. A `pending`/`failed`/`unavailable` depth is unselectable —
 * never silently substituted.
 *
 * Order is `off, basic, full`, mirroring the legacy button order in
 * `_renderShareStateButtons` (`PartnerSection.js:1016-1034`).
 *
 * @param {{readiness: {full?: string, basic?: string}}} unit
 * @returns {Array<'off'|'basic'|'full'>}
 */
export function offeredDepths(unit) {
  const offered = ['off'];
  for (const depth of DEPTH_ORDER) {
    if (unit.readiness?.[depth] === 'verified') offered.push(depth);
  }
  return offered;
}

/**
 * The detail panel's seed for a unit's STAGED value — what its switch and
 * radio rows show the moment the panel opens, before the user touches
 * anything: the committed state, else the suggested depth (`preselectDepth`),
 * else `'off'`.
 *
 * Clamped to an offered depth: a decided unit whose committed depth is no
 * longer `verified` (a register regeneration that failed, a partial poll
 * merge) would otherwise seed a value the switch has no segment for and
 * `submitConsent` would refuse. `'off'` is always offered, so the clamp
 * always has somewhere to land.
 *
 * @param {{state: ('full'|'basic'|'off'|null), readiness: {full?: string, basic?: string}}} unit
 * @returns {'full'|'basic'|'off'}
 */
export function seedStaged(unit) {
  const seed = unit.state ?? preselectDepth(unit) ?? 'off';
  return offeredDepths(unit).includes(seed) ? seed : 'off';
}

/**
 * How many of a unit's consumers are frozen guide snapshots. Pure count — the
 * panel builds the "Ongoing guidance + N guides" preview string from it, so
 * this module stays display-string-free.
 *
 * @param {{consumers?: Array<{type: string}>}} unit
 * @returns {number}
 */
export function frozenConsumerCount(unit) {
  return (unit.consumers || []).filter(c => c.type === 'frozen').length;
}

/**
 * The single muted sub-line a ledger row shows beneath its name, or `null`
 * for a unit with nothing to flag.
 *
 * Mutually exclusive by priority (addendum §3): a register revision
 * overrides "nothing available yet", which overrides a still-preparing Full
 * register, all on a never-reviewed unit. A
 * decided unit (`state != null`) never shows the preparing line, even
 * mid-generation — the "preparing" note only applies while the unit still
 * needs a first look. Consumer facts deliberately have NO row hint (owner
 * call 2026-08-25): they matter only mid-change, and the panel's "Where
 * it's used" section owns that moment.
 *
 * @param {{
 *   state: ('full'|'basic'|'off'|null),
 *   readiness: {full?: string},
 *   revisionReverted?: boolean
 * }} unit
 * @returns {null | {kind: 'revision'|'unavailable'|'preparing', text: string}}
 */
export function subLineFor(unit) {
  if (unit.revisionReverted) {
    return { kind: 'revision', text: 'Updated — needs another look' };
  }
  // Nothing verified at all: Private is the only offered depth, so say so
  // before the preparing line, whose "choose from what's available" would
  // otherwise point at nothing.
  if (unit.state == null && offeredDepths(unit).length === 1) {
    return { kind: 'unavailable', text: 'Nothing available to share yet — stays Private.' };
  }
  if (unit.state == null && unit.readiness?.full === 'pending') {
    return { kind: 'preparing', text: "Full version still preparing — Basic is ready now." };
  }
  return null;
}

/**
 * How many units in the ledger still need a first look.
 *
 * @param {Array<{state: ('full'|'basic'|'off'|null)}>} units
 * @returns {number}
 */
export function needsReviewCount(units) {
  return units.filter(u => u.state == null).length;
}

/**
 * The queue's traversal order for ONE group: unit **ids**, never object
 * snapshots — each queue step re-reads its unit from the live ledger by id,
 * which is what makes Back show a step answered earlier in the same run.
 *
 * The scope is a single group because the run ends in one atomic submit of
 * that group's whole disclosure set: a queue spanning two groups could not
 * be approved in one call.
 *
 * @param {Array<{id: string, groupId: string}>} units
 * @param {string} groupId - The group this run covers
 * @returns {Array<string>}
 */
export function queueScope(units, groupId) {
  return units.filter(u => u.groupId === groupId).map(u => u.id);
}

/**
 * The id of the ledger's gestalt group — the ongoing-guidance disclosure set,
 * which is the only group the real backend ships (`GESTALT_GROUP`) and the
 * default scope for "Review all". Falls back to the first group so the
 * two-group fixtures still open on something.
 *
 * @param {Array<{id: string, kind?: string}>} groups
 * @returns {string|null}
 */
export function gestaltGroupId(groups) {
  const list = groups || [];
  return (list.find(g => g.kind === 'gestalt') || list[0])?.id ?? null;
}

/**
 * The registry's ledger view of a sharing-status payload, or null when there
 * is nothing to consent to yet (locked / generating / error all arrive with
 * no share units — the host surface shows their reason instead).
 *
 * `confirmed` falls back to this user's own gestalt confirm when the payload's
 * group row doesn't carry one: the overview's Approve & share confirm IS
 * `gestaltConfirmedByYou`.
 *
 * Lives here rather than on `PartnerSection` because TWO hosts now mount
 * `SharingDetailPanel` off the same payload — the /partner registry and the
 * in-chat ceremony launcher (`chat/sharingCeremonyLauncher.js`). One mapping,
 * two hosts, so the view→ledger contract cannot drift.
 *
 * @param {Object|null} view - `getSharingStatus` payload (`res.data`)
 * @returns {Object|null} `{masters, groups, units}`
 */
export function ledgerFromStatus(view) {
  if (!view || !Array.isArray(view.units) || view.units.length === 0) return null;
  const groups = (Array.isArray(view.groups) ? view.groups : []).map(group => ({
    ...group,
    confirmed: typeof group.confirmed === 'boolean'
      ? group.confirmed
      : !!view.gestaltConfirmedByYou
  }));
  return {
    masters: { discovery: view.masters?.discovery !== false },
    groups,
    units: view.units
  };
}

/**
 * The batch-submit response guard — the ONE thing standing between a malformed
 * 200 and a "Shared" animation.
 *
 * `SharingDetailPanel._approveRun` treats ANY resolved `submitGroup` promise
 * as success (it closes itself and animates the rows), so a response carrying
 * no `units` array is a REJECTION, not a quiet no-op. Both hosts —
 * `PartnerSection._commitSharingSubmit` and the chat ceremony launcher — run
 * their submit through here, so the guard is shared code rather than prose
 * parity.
 *
 * @param {Object|null} res - `submitSharing` envelope (`{success, data}`)
 * @returns {Object} The canonical sharing view model
 * @throws {{error: string}} When the response carries no unit ledger
 */
export function ledgerFromSubmitResponse(res) {
  const view = res?.data;
  if (!view || !Array.isArray(view.units)) {
    throw { error: 'Could not save your choices. Please try again.' };
  }
  return view;
}
